import {ScrollService} from "../../common/scroll";
import {resolve} from "../../container";
import {ResizeObserverFactory} from "../../common/observation";
import {HorizontalOverflow, HorizontalOverflowInstaller} from "./horizontalOverflow";
import {Dictionary} from "./dictionary";
import {elementFrom} from "../../common/utils/html";
import {TrackingEventName} from "../../tracking/types";

export const HORIZONTAL_SCROLL_ERROR_MARGIN = 8;

export const SCROLL_DURATION = 250;

export class ScrollableBar {

    private scrollLeftButton: HTMLButtonElement;
    private scrollRightButton: HTMLButtonElement;
    private scrollableArea: HTMLElement;
    private items: HTMLElement[];

    private resizeObserver: ResizeObserver;
    private horizontalOverflow: HorizontalOverflow;

    public constructor(
        private navigationArea: HTMLElement,
        private dictionary: Dictionary,
        private scrollService: ScrollService = resolve(ScrollService),
        private resizeObserverFactory: ResizeObserverFactory = resolve(ResizeObserverFactory),
        private horizontalOverflowInstaller: HorizontalOverflowInstaller = resolve(HorizontalOverflowInstaller)
    ) {
        this.scrollableArea = this.navigationArea.querySelector(".horizontally-scrollable-area")!;
        this.scrollableArea.appendChild(elementFrom(`
            <div aria-hidden="true"><eop-swipe-hint></eop-swipe-hint></div>
        `));
        this.scrollableArea.addEventListener("scroll", () => this.toggleScrollButtonVisibility());
        this.scrollableArea.addEventListener("touchend", () => this.toggleScrollButtonVisibility(), {passive: true});

        this.items = Array.from(this.scrollableArea.querySelectorAll(".scrollable-item"));

        this.scrollLeftButton = elementFrom(`
            <button 
                class="scroll-button scroll-left"
                data-tracking-event-name="${TrackingEventName.NAVIGATION_INTERACT}"
                data-tracking-payload-text="scroll-left"
                aria-label="${this.dictionary.translate("MSG_SCROLL_LEFT")}"
                tabindex="-1"
            ></button>
        `);
        this.scrollLeftButton.appendChild(elementFrom(`<eop-svg-sprite-icon icon-id="left_arrow"></eop-svg-sprite-icon>`));
        this.scrollLeftButton.addEventListener("click", () => this.scrollHorizontallyLeft());
        this.navigationArea.appendChild(this.scrollLeftButton);

        this.scrollRightButton = elementFrom(`
            <button
                class="scroll-button scroll-right"
                data-tracking-event-name="${TrackingEventName.NAVIGATION_INTERACT}"
                data-tracking-payload-text="scroll-right"
                aria-label="${this.dictionary.translate("MSG_SCROLL_RIGHT")}"
                tabindex="-1"
            ></button>
        `);
        this.scrollRightButton.appendChild(elementFrom(`<eop-svg-sprite-icon icon-id="right_arrow"></eop-svg-sprite-icon>`));
        this.scrollRightButton.addEventListener("click", () => this.scrollHorizontallyRight());
        this.navigationArea.appendChild(this.scrollRightButton);

        this.horizontalOverflow = this.horizontalOverflowInstaller.installOn(this.scrollableArea);
        this.resizeObserver = this.resizeObserverFactory.create(() => this.toggleScrollButtonVisibility());
        this.resizeObserver.observe(this.scrollableArea);
    }

    public disconnect(): void {
        this.horizontalOverflow?.uninstall();
        this.resizeObserver.disconnect();
    }

    private scrollHorizontallyRight(): void {
        const viewPortOffset = this.scrollableArea.leftOffset();
        const innerRightEdge = this.innerRightEdge();

        const distanceToScroll = this.items
            .filter(item => item.rightOffset() > innerRightEdge)
            .map(item => item.leftOffset() - viewPortOffset - this.scrollButtonWidth())
            .find(scrollX => scrollX >= 1);

        this.scrollHorizontallyBy(distanceToScroll ?? this.scrollableArea.scrollWidth);
    }

    private scrollHorizontallyLeft(): void {
        const scrollableArea = this.scrollableArea;
        const viewPortOffset = scrollableArea.leftOffset();
        const firstPartiallyVisibleItem = this.firstPartiallyVisibleItemToLeft();
        const partiallyVisibleWidth = firstPartiallyVisibleItem.leftOffset() + firstPartiallyVisibleItem.measuredOuterWidth({includeMargin: true}) - this.innerLeftEdge();
        const remainingWidthToScrollInto = scrollableArea.clientWidth - partiallyVisibleWidth - (this.scrollButtonWidth() * 2);

        const reversedItems = this.items.inReverseOrder();
        const indexOfFirstPartiallyVisibleItem = reversedItems.indexOf(firstPartiallyVisibleItem);
        const firstNotFittingItem = reversedItems
            .filter((_item, index) => index > indexOfFirstPartiallyVisibleItem)
            .find(item => item.leftOffset() < -(remainingWidthToScrollInto + viewPortOffset));
        if (firstNotFittingItem) {
            const indexOfFirstNotFittingItem = reversedItems.indexOf(firstNotFittingItem);
            if (indexOfFirstNotFittingItem === 0) {
                this.scrollHorizontallyBy(firstNotFittingItem.leftOffset() - viewPortOffset - this.scrollButtonWidth());
            } else {
                const prevItem = reversedItems[indexOfFirstNotFittingItem - 1];
                this.scrollHorizontallyBy(prevItem.leftOffset() - viewPortOffset - this.scrollButtonWidth());
            }
        } else {
            this.scrollHorizontallyTo(0);
        }
    }

    private firstPartiallyVisibleItemToLeft(): HTMLElement {
        const firstObscuredItem = this.items.inReverseOrder()
            .filter(item => item.leftOffset() < this.innerLeftEdge())
            .getFirst();

        return firstObscuredItem || this.items[0];
    }

    private innerLeftEdge(): number {
        const scrollLeftButton = this.scrollLeftButton;
        return scrollLeftButton ? scrollLeftButton.rightOffset() : this.scrollableArea.leftOffset();
    }

    private innerRightEdge(): number {
        const scrollRightButton = this.scrollRightButton;
        return scrollRightButton?.leftOffset() ?? this.scrollableArea.rightOffset();
    }

    private scrollHorizontallyBy(scrollX: number): void {
        this.scrollHorizontallyTo(scrollX + this.scrollableArea.scrollLeft);
    }

    private scrollHorizontallyTo(scrollX: number): void {
        void this.scrollService.enclosingHorizontalArea(this.scrollableArea).scrollTo(scrollX, SCROLL_DURATION);
    }

    private scrollButtonWidth(): number {
        const scrollLeftButton = this.scrollLeftButton;
        const scrollRightButton = this.scrollRightButton;
        return scrollLeftButton?.clientWidth ?? scrollRightButton?.clientWidth ?? 0;
    }

    private toggleScrollButtonVisibility(): void {
        const scrollableArea = this.scrollableArea;
        const scrollLeft = scrollableArea.scrollLeft;
        const scrollWidth = scrollableArea.scrollWidth;
        const elementWidth = scrollableArea.clientWidth;

        if (elementWidth + HORIZONTAL_SCROLL_ERROR_MARGIN >= scrollWidth) {
            this.scrollRightButton.classList.remove("active");
            this.scrollLeftButton.classList.remove("active");
            return;
        }

        this.scrollRightButton.classList.toggle("active", scrollLeft + elementWidth + HORIZONTAL_SCROLL_ERROR_MARGIN < scrollWidth);
        this.scrollLeftButton.classList.toggle("active", scrollLeft > HORIZONTAL_SCROLL_ERROR_MARGIN);
    }

    public scrollToItem(index: number): void {
        void this.scrollService.enclosingHorizontalArea(this.scrollableArea)
            .scrollToElementCentered(this.items[index], SCROLL_DURATION);
    }
}
